import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Brain, TrendingUp, AlertCircle, CheckCircle, Target,
  DollarSign, Users, Shield, Award, Briefcase, PieChart, BarChart3
} from 'lucide-react';
import { Layout } from '../components/Layout/Layout';
import { supabase } from '../lib/supabase';
import { PieChart as RechartsPie, Pie, Cell, ResponsiveContainer, RadialBarChart, RadialBar, Legend } from 'recharts';
import type { EngineFullResult } from '../financial-engine/types';

interface Project {
  id: string;
  name: string;
  description: string | null;
  status: string;
  domain: string;
  funding_requested: number;
  funding_amount: number;
  owner_name: string;
  owner_organization: string;
  owner_email: string;
}

interface FinancingAnalysis {
  id: string;
  overall_score: number;
  bankability_grade: 'A' | 'B' | 'C' | 'D';
  recommendation: 'APPROVED' | 'CONDITIONAL' | 'REVIEW' | 'REJECTED';
  economic_score: number;
  financial_security_score: number;
  social_impact_score: number;
  governance_score: number;
  strengths: Array<{ category: string; message: string }>;
  concerns: Array<{ category: string; severity: string; message: string }>;
  financial_data: any;
  created_at: string;
  engine_result?: EngineFullResult;
}

export const FunderProjectDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [analysis, setAnalysis] = useState<FinancingAnalysis | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadProjectAndAnalysis();
    }
  }, [id]);

  const loadProjectAndAnalysis = async () => {
    try {
      setIsLoading(true);

      const { data: projectData, error: projectError } = await supabase
        .from('projects')
        .select(`
          id,
          name,
          description,
          status,
          domain,
          funding_requested,
          funding_amount,
          owner:users!projects_owner_id_fkey(full_name, organization, email)
        `)
        .eq('id', id)
        .maybeSingle();

      if (projectError) throw projectError;
      if (!projectData) {
        navigate('/funder/projects');
        return;
      }

      setProject({
        id: projectData.id,
        name: projectData.name,
        description: projectData.description,
        status: projectData.status,
        domain: projectData.domain,
        funding_requested: projectData.funding_requested || 0,
        funding_amount: projectData.funding_amount || 0,
        owner_name: (projectData.owner as any)?.full_name || 'Non renseigné',
        owner_organization: (projectData.owner as any)?.organization || '',
        owner_email: (projectData.owner as any)?.email || ''
      });

      const { data: analysisData } = await supabase
        .from('financing_analyses')
        .select('*')
        .eq('project_id', id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (analysisData) {
        setAnalysis(analysisData as any);
      }
    } catch (error) {
      console.error('Error loading project:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const generateAnalysis = async () => {
    if (!project || !id) return;

    try {
      setIsGenerating(true);
      setGenerationError(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const apiUrl = `${supabaseUrl}/functions/v1/run-financial-engine`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ projectId: id })
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = errorData.error || 'Failed to generate analysis';

        if (errorMessage.includes('Aucun plan financier') || errorMessage.includes('plan financier trouvé')) {
          setGenerationError('MISSING_BUSINESS_PLAN');
          return;
        }

        throw new Error(errorMessage);
      }

      const engineResult: EngineFullResult = await response.json();

      const financialScore = engineResult.score.score_numeric;
      let bankabilityGrade: 'A' | 'B' | 'C' | 'D';
      let recommendation: 'APPROVED' | 'CONDITIONAL' | 'REVIEW' | 'REJECTED';

      if (financialScore >= 80) {
        bankabilityGrade = 'A';
        recommendation = 'APPROVED';
      } else if (financialScore >= 65) {
        bankabilityGrade = 'B';
        recommendation = 'CONDITIONAL';
      } else if (financialScore >= 50) {
        bankabilityGrade = 'C';
        recommendation = 'REVIEW';
      } else {
        bankabilityGrade = 'D';
        recommendation = 'REJECTED';
      }

      const strengths = engineResult.recommendations
        .filter(r => r.startsWith('🟢'))
        .map(r => ({ category: 'strength', message: r }));

      const concerns = engineResult.recommendations
        .filter(r => r.startsWith('🔴') || r.startsWith('⚠️') || r.startsWith('🚨'))
        .map(r => {
          const severity = r.startsWith('🚨') ? 'critical' : r.startsWith('🔴') ? 'high' : 'medium';
          return { category: 'concern', severity, message: r };
        });

      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('Not authenticated');

      const { data: savedAnalysis, error: saveError } = await supabase
        .from('financing_analyses')
        .insert({
          project_id: id,
          analyst_id: user.user.id,
          overall_score: financialScore,
          bankability_grade: bankabilityGrade,
          recommendation: recommendation,
          economic_score: engineResult.analysis.gross_margin_rate_pct,
          financial_security_score: Math.min(100, (engineResult.analysis.runway_months / 6) * 100),
          social_impact_score: 75,
          governance_score: 75,
          strengths: strengths,
          concerns: concerns,
          financial_data: engineResult.normalizedModel
        })
        .select()
        .single();

      if (saveError) throw saveError;

      setAnalysis({ ...savedAnalysis as any, engine_result: engineResult });
      setGenerationError(null);
    } catch (error) {
      console.error('Error generating analysis:', error);

      if (error instanceof Error && (error.message.includes('Aucun plan financier') || error.message.includes('plan financier trouvé'))) {
        setGenerationError('MISSING_BUSINESS_PLAN');
      } else {
        alert('Erreur lors de la génération de l\'analyse: ' + (error instanceof Error ? error.message : 'Unknown error'));
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const getGradeColor = (grade: string) => {
    const colors = {
      A: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
      B: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
      C: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' },
      D: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' }
    };
    return colors[grade as keyof typeof colors] || colors.C;
  };

  const getRecommendationLabel = (rec: string) => {
    const labels = {
      APPROVED: { text: 'Financement recommandé', color: 'text-green-700', bg: 'bg-green-100' },
      CONDITIONAL: { text: 'Financement sous conditions', color: 'text-blue-700', bg: 'bg-blue-100' },
      REVIEW: { text: 'Nécessite examen approfondi', color: 'text-yellow-700', bg: 'bg-yellow-100' },
      REJECTED: { text: 'Financement non recommandé', color: 'text-red-700', bg: 'bg-red-100' }
    };
    return labels[rec as keyof typeof labels] || labels.REVIEW;
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
      case 'high':
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      case 'medium':
        return <AlertCircle className="w-4 h-4 text-yellow-600" />;
      default:
        return <AlertCircle className="w-4 h-4 text-blue-600" />;
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center min-h-96">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!project) {
    return (
      <Layout>
        <div className="p-6">
          <p className="text-gray-500">Projet introuvable</p>
        </div>
      </Layout>
    );
  }

  const scoreData = analysis ? [
    { name: 'Viabilité\néconomique', value: analysis.economic_score, fill: '#2F47FF' },
    { name: 'Sécurité\nfinancière', value: analysis.financial_security_score, fill: '#6B4EFF' },
    { name: 'Impact\nsocial', value: analysis.social_impact_score, fill: '#8B5CF6' },
    { name: 'Gouvernance', value: analysis.governance_score, fill: '#A78BFA' }
  ] : [];

  return (
    <Layout>
      <div className="p-6 space-y-6" style={{ background: '#F7F8FC' }}>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/funder/projects')}
            className="p-2 hover:bg-white rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{project.name}</h1>
            <p className="text-gray-600 mt-1">Analyse financière du projet ESS</p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Informations du projet</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-gray-500 mb-1">Porteur de projet</p>
              <p className="text-base font-medium text-gray-900">{project.owner_name}</p>
              {project.owner_organization && (
                <p className="text-sm text-gray-600">{project.owner_organization}</p>
              )}
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Financement demandé</p>
              <p className="text-2xl font-bold" style={{ color: '#2F47FF' }}>
                {project.funding_requested.toLocaleString('fr-FR')} €
              </p>
            </div>
          </div>
          {project.description && (
            <div className="mt-4">
              <p className="text-sm text-gray-500 mb-1">Description</p>
              <p className="text-gray-700">{project.description}</p>
            </div>
          )}
        </div>

        {!analysis ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
            {generationError === 'MISSING_BUSINESS_PLAN' ? (
              <>
                <AlertCircle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Business plan non fourni</h3>
                <p className="text-gray-600 mb-4">
                  Le porteur de projet n'a pas encore fourni de plan financier détaillé.
                </p>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6 text-left">
                  <p className="text-sm text-gray-700 mb-2">
                    <strong>Informations manquantes pour l'analyse :</strong>
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                    <li>Prévisions de chiffre d'affaires (mensuel/annuel)</li>
                    <li>Charges prévisionnelles (fixes, variables, masse salariale)</li>
                    <li>Plan de trésorerie</li>
                    <li>Investissements initiaux (CAPEX)</li>
                  </ul>
                </div>
                <p className="text-sm text-gray-600 mb-6">
                  Invitez le porteur de projet à compléter son business plan dans l'onglet "Plan d'Affaires" pour permettre une analyse financière approfondie.
                </p>
                <button
                  onClick={() => setGenerationError(null)}
                  className="px-6 py-3 rounded-lg font-medium transition-colors"
                  style={{ background: '#E5E7EB', color: '#374151' }}
                >
                  Fermer
                </button>
              </>
            ) : (
              <>
                <Brain className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucune analyse disponible</h3>
                <p className="text-gray-600 mb-6">
                  Générez une analyse automatisée de ce projet pour évaluer sa viabilité financière.
                </p>
                <button
                  onClick={generateAnalysis}
                  disabled={isGenerating}
                  className="px-6 py-3 rounded-lg font-medium text-white transition-colors disabled:opacity-50"
                  style={{ background: '#2F47FF' }}
                >
                  {isGenerating ? 'Génération en cours...' : 'Générer l\'analyse IA'}
                </button>
              </>
            )}
          </div>
        ) : (
          <>
            <div
              className="rounded-xl shadow-sm border border-gray-100 overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, #2F47FF 0%, #6B4EFF 100%)'
              }}
            >
              <div className="p-6 text-white">
                <div className="flex items-center space-x-3 mb-2">
                  <Brain className="w-6 h-6" />
                  <h2 className="text-xl font-bold">Analyse IA – Moteur CRESS</h2>
                </div>
                <p className="text-blue-100">
                  Analyse automatisée générée le {new Date(analysis.created_at).toLocaleDateString('fr-FR')}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-2">
                  <Target className="w-5 h-5 text-gray-400" />
                  <span className={`text-3xl font-bold ${getGradeColor(analysis.bankability_grade).text}`}>
                    {analysis.bankability_grade}
                  </span>
                </div>
                <p className="text-sm font-medium text-gray-600">Note globale</p>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-2">
                  <BarChart3 className="w-5 h-5 text-gray-400" />
                  <span className="text-3xl font-bold" style={{ color: '#2F47FF' }}>
                    {Math.round(analysis.overall_score)}
                  </span>
                </div>
                <p className="text-sm font-medium text-gray-600">Score global /100</p>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-2">
                  <TrendingUp className="w-5 h-5 text-gray-400" />
                  <span className="text-3xl font-bold text-green-600">
                    {Math.round(analysis.economic_score)}
                  </span>
                </div>
                <p className="text-sm font-medium text-gray-600">Viabilité économique</p>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-2">
                  <Shield className="w-5 h-5 text-gray-400" />
                  <span className="text-3xl font-bold text-blue-600">
                    {Math.round(analysis.financial_security_score)}
                  </span>
                </div>
                <p className="text-sm font-medium text-gray-600">Sécurité financière</p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className={`px-4 py-3 rounded-lg ${getRecommendationLabel(analysis.recommendation).bg}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Award className={`w-5 h-5 ${getRecommendationLabel(analysis.recommendation).color}`} />
                    <span className={`font-semibold ${getRecommendationLabel(analysis.recommendation).color}`}>
                      Recommandation : {getRecommendationLabel(analysis.recommendation).text}
                    </span>
                  </div>
                  <span className={`text-2xl font-bold ${getRecommendationLabel(analysis.recommendation).color}`}>
                    {Math.round(analysis.overall_score)}%
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                  <PieChart className="w-5 h-5" style={{ color: '#2F47FF' }} />
                  <span>Répartition des scores</span>
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPie>
                      <Pie
                        data={scoreData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, value }) => `${name}: ${value}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {scoreData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                    </RechartsPie>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span>Points forts ({analysis.strengths.length})</span>
                </h3>
                <div className="space-y-3">
                  {analysis.strengths.slice(0, 5).map((strength, idx) => (
                    <div key={idx} className="flex items-start space-x-3 p-3 bg-green-50 rounded-lg">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-gray-700">{strength.message}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {analysis.concerns.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                  <AlertCircle className="w-5 h-5 text-orange-600" />
                  <span>Points d'attention ({analysis.concerns.length})</span>
                </h3>
                <div className="space-y-3">
                  {analysis.concerns.map((concern, idx) => (
                    <div key={idx} className="flex items-start space-x-3 p-3 bg-orange-50 rounded-lg">
                      {getSeverityIcon(concern.severity)}
                      <div className="flex-1">
                        <p className="text-sm text-gray-700">{concern.message}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Sévérité : {concern.severity === 'critical' ? 'Critique' : concern.severity === 'high' ? 'Élevée' : concern.severity === 'medium' ? 'Moyenne' : 'Faible'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {analysis.engine_result && (
              <>
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                    <TrendingUp className="w-5 h-5" style={{ color: '#2F47FF' }} />
                    <span>Analyse financière détaillée</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Marge brute</p>
                      <p className="text-2xl font-bold text-blue-700">
                        {analysis.engine_result.analysis.gross_margin_rate_pct.toFixed(1)}%
                      </p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Runway (trésorerie)</p>
                      <p className="text-2xl font-bold text-green-700">
                        {Math.round(analysis.engine_result.analysis.runway_months)} mois
                      </p>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Burn Rate mensuel</p>
                      <p className="text-2xl font-bold text-purple-700">
                        {analysis.engine_result.analysis.burn_rate_monthly.toLocaleString('fr-FR')} €
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                    <Shield className="w-5 h-5 text-orange-600" />
                    <span>Tests de stress</span>
                  </h3>
                  <div className="space-y-4">
                    <div className="p-4 border border-gray-200 rounded-lg">
                      <p className="font-semibold text-gray-900 mb-2">Scénario A : Baisse CA de 20%</p>
                      <p className="text-sm text-gray-700 mb-1">
                        Runway : <span className="font-medium">{analysis.engine_result.stress.scenario_A_lowerRevenue.runway_months.toFixed(1)} mois</span>
                      </p>
                      <p className="text-sm text-gray-600">{analysis.engine_result.stress.scenario_A_lowerRevenue.comment}</p>
                    </div>
                    <div className="p-4 border border-gray-200 rounded-lg">
                      <p className="font-semibold text-gray-900 mb-2">Scénario B : Hausse masse salariale de 15%</p>
                      <p className="text-sm text-gray-700 mb-1">
                        Runway : <span className="font-medium">{analysis.engine_result.stress.scenario_B_higherPayroll.runway_months.toFixed(1)} mois</span>
                      </p>
                      <p className="text-sm text-gray-600">{analysis.engine_result.stress.scenario_B_higherPayroll.comment}</p>
                    </div>
                    <div className="p-4 border border-gray-200 rounded-lg">
                      <p className="font-semibold text-gray-900 mb-2">Scénario C : Délai d'encaissement</p>
                      <p className="text-sm text-gray-700 mb-1">
                        Runway : <span className="font-medium">{analysis.engine_result.stress.scenario_C_cashDelay.runway_months.toFixed(1)} mois</span>
                      </p>
                      <p className="text-sm text-gray-600">{analysis.engine_result.stress.scenario_C_cashDelay.comment}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                    <Users className="w-5 h-5" style={{ color: '#6B4EFF' }} />
                    <span>Comparaison sectorielle</span>
                  </h3>
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Secteur :</span> {analysis.engine_result.peersComparison.sector}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">Marge brute moyenne du secteur</p>
                        <p className="text-lg font-bold text-gray-900">
                          {analysis.engine_result.peersComparison.peer_average_gross_margin_pct.toFixed(1)}%
                        </p>
                        <p className="text-xs text-gray-600 mt-1">
                          Écart : {analysis.engine_result.peersComparison.delta_margin_vs_peer_pct_points > 0 ? '+' : ''}
                          {analysis.engine_result.peersComparison.delta_margin_vs_peer_pct_points.toFixed(1)} pts
                        </p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">Masse salariale / CA moyenne</p>
                        <p className="text-lg font-bold text-gray-900">
                          {analysis.engine_result.peersComparison.peer_average_payroll_vs_revenue_pct.toFixed(1)}%
                        </p>
                        <p className="text-xs text-gray-600 mt-1">
                          Écart : {analysis.engine_result.peersComparison.delta_payroll_vs_peer_pct_points > 0 ? '+' : ''}
                          {analysis.engine_result.peersComparison.delta_payroll_vs_peer_pct_points.toFixed(1)} pts
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 space-y-2">
                      {analysis.engine_result.peersComparison.commentary.map((comment, idx) => (
                        <p key={idx} className="text-sm text-gray-700 p-2 bg-blue-50 rounded">{comment}</p>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                    <Award className="w-5 h-5 text-yellow-600" />
                    <span>Score de santé financière</span>
                  </h3>
                  <div className="flex items-center space-x-6 mb-4">
                    <div className="flex-shrink-0">
                      <div className="text-5xl font-bold" style={{ color: '#2F47FF' }}>
                        {analysis.engine_result.score.score_numeric}/100
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{analysis.engine_result.score.label}</p>
                    </div>
                    <div className="flex-1 space-y-2">
                      {analysis.engine_result.score.rationale.map((reason, idx) => (
                        <p key={idx} className="text-sm text-gray-700">{reason}</p>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Synthèse automatique</h3>
              <div className="space-y-4 text-gray-700">
                <p>
                  Ce projet présente un <span className="font-semibold">score global de {Math.round(analysis.overall_score)}/100</span>,
                  ce qui correspond à une note <span className={`font-bold ${getGradeColor(analysis.bankability_grade).text}`}>{analysis.bankability_grade}</span>.
                </p>
                <p>
                  La viabilité économique est évaluée à <span className="font-semibold">{Math.round(analysis.economic_score)}%</span>,
                  la sécurité financière à <span className="font-semibold">{Math.round(analysis.financial_security_score)}%</span>,
                  l'impact social à <span className="font-semibold">{Math.round(analysis.social_impact_score)}%</span> et
                  la gouvernance à <span className="font-semibold">{Math.round(analysis.governance_score)}%</span>.
                </p>
                <p>
                  Le projet compte <span className="font-semibold">{analysis.strengths.length} points forts</span> identifiés
                  et <span className="font-semibold">{analysis.concerns.length} points d'attention</span> nécessitant un suivi.
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};
